import React from 'react';

import { registerHandlers, removeAndStopHandlers } from '../../sagas/TaskManager';
import { createDomainModule } from '../index';

import * as CollectionActions from './actions';
import * as CollectionConstants from './constants';
import * as D from './deps';
import {
  CollectionFilterTransform,
  CollectionReducer,
  RecentlyPlayedItemsReducer,
} from './reducer';
import * as OwnWorkers from './sagas/workers';
import * as CollectionUtils from './utils';

export default createDomainModule({
  name: CollectionConstants.DOMAIN_NAME,
  actions: CollectionActions,
  constants: CollectionConstants,
  utils: CollectionUtils,
  components: {
    AddedHeader: (props: $TSFixMe) => {
      return (
        <D.AsyncComponent
          {...props}
          importer={() =>
            System.import(
              /* webpackChunkName: "c_added-header" */ './components/MyMusic/AddedHeader/AddedHeader',
            )
          }
        />
      );
    },
    AddToPlaylistSubmenu: (props: $TSFixMe) => {
      return (
        <D.AsyncComponent
          {...props}
          importer={() =>
            System.import(
              /* webpackChunkName: "c_add-to-playlist-submenu" */ './components/AddToPlaylistSubmenu/AddToPlaylistSubmenu',
            )
          }
        />
      );
    },
    CollectionButton: (props: $TSFixMe) => {
      return (
        <D.AsyncComponent
          {...props}
          importer={() =>
            System.import(
              /* webpackChunkName: "c_collection-button" */ './components/CollectionButton/CollectionButton',
            )
          }
        />
      );
    },
    ArtistRowItem: (props: $TSFixMe) => {
      return (
        <D.AsyncComponent
          {...props}
          importer={() =>
            System.import(
              /* webpackChunkName: "c_artist-row-item" */ './components/MyMusic/ArtistRowItem/ArtistRowItem',
            )
          }
        />
      );
    },
    MyMusicArtistPage: (props: $TSFixMe) => {
      return (
        <D.AsyncComponent
          {...props}
          importer={() =>
            System.import(
              /* webpackChunkName: "c_my-music-artist-page" */ './components/MyMusic/MyMusicArtistPage/MyMusicArtistPage',
            )
          }
        />
      );
    },
    MyMusicEmptyState: (props: $TSFixMe) => {
      return (
        <D.AsyncComponent
          {...props}
          importer={() =>
            System.import(
              /* webpackChunkName: "c_my-music-empty-state" */ './components/MyMusic/MyMusicEmptyState/MyMusicEmptyState',
            )
          }
        />
      );
    },
    MyMusicGrid: (props: $TSFixMe) => {
      return (
        <D.AsyncComponent
          {...props}
          importer={() =>
            System.import(
              /* webpackChunkName: "c_my-music-grid" */ './components/MyMusic/MyMusicGrid/MyMusicGrid',
            )
          }
        />
      );
    },
    MyMusicContainer: (props: $TSFixMe) => {
      return (
        <D.AsyncComponent
          {...props}
          importer={() =>
            System.import(
              /* webpackChunkName: "c_my-music-container" */ './containers/MyMusic/MyMusic',
            )
          }
        />
      );
    },
    RecommendedPodcastsAndStations: (props: $TSFixMe) => {
      return (
        <D.AsyncComponent
          {...props}
          importer={() =>
            System.import(
              /* webpackChunkName: "c_recommended-podcasts-and-stations" */ './components/MyMusic/RecommendedPodcastsAndStations/RecommendedPodcastsAndStations',
            )
          }
        />
      );
    },
  },
  reducer: {
    collection: CollectionReducer,
    recentlyPlayedItems: RecentlyPlayedItemsReducer,
  },
  persisted: true,
  persistedReducers: ['collection', 'recentlyPlayedItems'],
  // @ts-expect-error
  persistTransforms: [CollectionFilterTransform],
  onLoad(store: $TSFixMe): $TSFixMe {
    (this as $TSFixMe).runSaga(store, OwnWorkers.onDomainLoad);
  },
  onStart(_store: $TSFixMe): $TSFixMe {
    (this as $TSFixMe)._handlers = registerHandlers([
      {
        actionType: D.Constants.Listener.ListenerActions.LISTENER_AUTHENTICATED,
        handler: OwnWorkers.initialize,
        concur: 'takeLatest',
      },
      {
        actionType: D.ApiActions.COLLECTIONS_GET_ITEMS_SUCCESS,
        handler: OwnWorkers.onGetItemsSuccess,
        concur: 'takeEvery',
      },
      {
        actionType: D.ApiActions.COLLECTIONS_ADD_ITEM_SUCCESS,
        handler: OwnWorkers.onAddItemSuccess,
        concur: 'takeEvery',
      },
      {
        actionType: D.ApiActions.COLLECTIONS_REMOVE_ITEM_SUCCESS,
        handler: OwnWorkers.onRemoveItemSuccess,
        concur: 'takeEvery',
      },
      {
        actionType: D.CollectionActions.ADD_COLLECTION_ITEM,
        handler: OwnWorkers.onAddCollectionItem,
        concur: 'takeEvery',
      },
      {
        actionType: D.CollectionActions.REMOVE_COLLECTION_ITEM,
        handler: OwnWorkers.onRemoveCollectionItem,
        concur: 'takeEvery',
      },
      {
        actionType: D.CollectionActions.GET_COLLECTION_SORTED_ITEMS,
        handler: OwnWorkers.onGetSortedItems,
        concur: 'takeEvery',
      },
      {
        actionType: D.CollectionActions.GET_COLLECTION_ITEMS_BY_ARTIST,
        handler: OwnWorkers.onGetItemsByArtist,
        concur: 'takeEvery',
      },
      {
        actionType: D.ApiActions.PLAYLISTS_APPEND_ITEMS_SUCCESS,
        handler: OwnWorkers.onPlaylistAppendItemsSuccess,
        concur: 'takeEvery',
      },
      {
        actionType: D.ApiActions.PLAYLISTS_CREATE_SUCCESS,
        handler: OwnWorkers.onPlaylistUpdateSuccess,
        concur: 'takeEvery',
      },
      {
        actionType: D.ApiActions.PLAYLISTS_DELETE_TRACKS_SUCCESS,
        handler: OwnWorkers.onPlaylistUpdateSuccess,
        concur: 'takeEvery',
      },
      {
        actionType: D.ApiActions.PLAYLISTS_EDIT_TRACKS_SUCCESS,
        handler: OwnWorkers.onPlaylistUpdateSuccess,
        concur: 'takeEvery',
      },
      {
        actionType: D.ApiActions.PLAYLISTS_SET_DETAILS_SUCCESS,
        handler: OwnWorkers.onPlaylistUpdateSuccess,
        concur: 'takeEvery',
      },
      {
        actionType: D.ApiActions.COLLECTIONS_ADD_ITEM_SUCCESS,
        handler: OwnWorkers.onCollectionUpdateSuccess,
        concur: 'takeEvery',
      },
      {
        actionType: D.ApiActions.COLLECTIONS_REMOVE_ITEM_SUCCESS,
        handler: OwnWorkers.onCollectionUpdateSuccess,
        concur: 'takeEvery',
      },
      {
        actionType: D.ApiActions.PLAYLISTS_CREATE_SUCCESS,
        handler: OwnWorkers.onCollectionUpdateSuccess,
        concur: 'takeEvery',
      },
      {
        actionType: D.ApiActions.COLLECTIONS_GET_ITEMS_SUCCESS,
        handler: OwnWorkers.onCollectionUpdateSuccess,
        concur: 'takeEvery',
      },
      {
        actionType: CollectionConstants.CollectionActions.LOAD_FILTER_AT_CURRENT_OFFSET,
        handler: OwnWorkers.onLoadFilterAtCurrentOffset,
        concur: 'takeEvery',
      },
      {
        actionType: D.ApiActions.COLLECTIONS_GET_VERSION_SUCCESS,
        handler: OwnWorkers.onGetCollectionVersionSuccess,
        concur: 'takeEvery',
      },
      {
        actionType: D.Constants.Playback.ActionTypes.TRACK_THUMBUP,
        handler: OwnWorkers.callGetCollectionVersion,
        concur: 'takeEvery',
      },
      {
        actionType: D.Constants.Playback.ActionTypes.TRACK_REMOVE_THUMBUP,
        handler: OwnWorkers.callGetCollectionVersion,
        concur: 'takeEvery',
      },
      {
        actionType: D.CollectionActions.SET_COLLECTION_CURRENT_FILTER,
        handler: OwnWorkers.callGetCollectionVersion,
        concur: 'takeEvery',
      },
      {
        actionType: D.CollectionActions.GET_COLLECTION_VERSION,
        handler: OwnWorkers.onGetCollectionVersion,
        concur: 'takeLatest',
      },
      {
        actionType: D.ApiActions.CREATE_STATION_SUCCESS,
        handler: OwnWorkers.onStationCreated,
        concur: 'takeEvery',
      },
      {
        actionType: [
          D.Constants.Integrations.ActionTypes.TRACK_STARTED_FROM_SOURCE,
          D.Constants.Playback.ActionTypes.STATION_TRACK_RESUMED_FROM_SOURCE,
        ],
        handler: OwnWorkers.onSPSTrackStart,
        concur: 'takeLatest',
      },
      {
        actionType: D.ApiActions.REMOVE_STATION_SUCCESS,
        handler: OwnWorkers.onRemoveRecentlyPlayedItem,
        concur: 'takeLatest',
      },
      {
        actionType: D.GraphQL.Constants.OperationNameActions.GRAPHQL_REMOVE_FROM_LIBRARY_SUCCESS,
        handler: OwnWorkers.onRemoveRecentlyPlayedItem,
        concur: 'takeLatest',
      },
      {
        actionType: D.Constants.PlaylistT3.ActionTypes.CONFIRM_DELETE_PLAYLIST,
        handler: OwnWorkers.onRemoveRecentlyPlayedItem,
        concur: 'takeLatest',
      },
      {
        actionType: CollectionConstants.CollectionActions.OPEN_PREMIUM_WELCOME_MODAL,
        handler: OwnWorkers.openPremiumWelcomeModal,
        concur: 'takeOnce',
      },
      {
        actionType: D.Constants.App.AppActions.PAGE_VISIBILITY_DID_CHANGE,
        handler: OwnWorkers.onVisibilityChange,
        concur: 'takeLatest',
      },
      {
        actionType: [
          D.ApiActions.COLLECTIONS_GET_SORTED_BY_TYPES_SUCCESS,
          D.ApiActions.COLLECTIONS_GET_SORTED_BY_TYPES_V6_SUCCESS,
          D.ApiActions.COLLECTIONS_GET_SORTED_PLAYLISTS_SUCCESS,
        ],
        handler: OwnWorkers.fetchPlaylistCurators,
        concur: 'takeLatest',
      },
    ]);
  },
  onStop() {
    removeAndStopHandlers((this as $TSFixMe)._handlers, (this as $TSFixMe).name);
  },
});
